import {
  BlockStack,
  Box,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import type { AuditLogRow } from "./auditLogTypes";
import { AuditActionBadge } from "./audit-action-badge";
import { AuditLogTime } from "./audit-log-time";
import { AuditProvenanceBlock } from "./audit-provenance-block";
import { AuditChangesTrigger, hasAuditChangePayload } from "./audit-changes-trigger";
import { AuditSubjectLine } from "./audit-subject-line";
import { User, Terminal } from "lucide-react";

type Props = {
  companyId: string;
  row: AuditLogRow;
  /**
   * Global (Master) list: show which entity. On an entity page, set false
   * to save space; subject is implied by the page.
   */
  showSubjectLine?: boolean;
  /**
   * Show raw JSON changes and request provenance (IP, route, etc.).
   * In a global list, this is usually true. In an entity sidebar/tab,
   * it's often better to keep it false (cleaner view).
   */
  showDetails?: boolean;
  /**
   * Show request provenance (IP, route, etc.).
   * Usually true only in the global settings audit logs list.
   */
  showProvenance?: boolean;
  /** e.g. subtle padding for list rows */
  padded?: boolean;
  /** Whether to show "Open" link to the entity. Usually true in global logs, false on entity pages. */
  showOpenLink?: boolean;
};

const getInitials = (name: string) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Generate a soft background color based on the username string
const getAvatarBgColor = (name: string) => {
  if (!name) return "bg-slate-100 text-slate-500";
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    "bg-indigo-100 text-indigo-700 border-indigo-200",
    "bg-sky-100 text-sky-700 border-sky-200",
    "bg-emerald-100 text-emerald-700 border-emerald-200",
    "bg-amber-100 text-amber-700 border-amber-200",
    "bg-violet-100 text-violet-700 border-violet-200",
    "bg-rose-100 text-rose-700 border-rose-200",
    "bg-teal-100 text-teal-700 border-teal-200"
  ];
  return colors[Math.abs(hash) % colors.length];
};

export function AuditLogEntry({
  companyId,
  row,
  showSubjectLine = true,
  showDetails = true,
  showProvenance = false,
  padded = true,
  showOpenLink = false,
}: Props) {
  const { t } = useTranslation();
  const meta = row.metadata && typeof row.metadata === "object" ? row.metadata as Record<string, unknown> : null;

  const performerName = row.userName || row.userId || "";
  const initials = getInitials(performerName);
  const avatarClass = getAvatarBgColor(performerName);
  const hasChanges = hasAuditChangePayload(row.changes);

  const content = (
    <BlockStack gap="400">
      {/* Upper section: Performer info, badge, and time */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Stylized Avatar */}
          <div className={`w-8 h-8 rounded-full border flex items-center justify-center font-semibold text-xs shadow-sm shrink-0 ${avatarClass}`}>
            {initials ? initials : <User className="w-4 h-4" />}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-slate-700 truncate">
              {row.userName ? row.userName : row.userId ? row.userId : (
                <span className="text-slate-400 font-medium italic flex items-center gap-1">
                  <Terminal className="w-3.5 h-3.5" />
                  {t("audit_actor_system", { defaultValue: "System Process" })}
                </span>
              )}
            </span>
            <span className="text-xs text-slate-400 mt-0.5">
              <AuditLogTime createdAt={row.createdAt} />
            </span>
          </div>
        </div>

        {/* Action Badge */}
        <div className="self-start sm:self-center">
          <AuditActionBadge action={row.action} />
        </div>
      </div>

      {/* Main content body. Below sm the boxes nest in a popover about 330 px wide, so their padding is slimmer. */}
      <div className="bg-slate-50/50 rounded-xl p-3.5 max-sm:p-2 border border-slate-100/80">
        <BlockStack gap="200">
          {showSubjectLine && (
            <div className="text-slate-800 text-sm font-medium">
              <AuditSubjectLine
                companyId={companyId}
                subjectType={row.subjectType}
                subjectId={row.subjectId}
                subjectName={row.subjectName}
                clientRoute={meta?.clientRoute as string | undefined}
                showOpenLink={showOpenLink}
              />
            </div>
          )}

          {row.description && (
            <p className="text-sm text-slate-600 font-normal leading-relaxed bg-white border border-slate-100 rounded-lg p-2.5 max-sm:p-2 shadow-sm">
              {row.description}
            </p>
          )}
        </BlockStack>
      </div>

      {/* Details/Diff and context fields */}
      {(showProvenance || (showDetails && hasChanges)) && (
        <div className="flex flex-col gap-3 pt-1">
          {showProvenance && (
            <AuditProvenanceBlock
              metadata={
                meta
                  ? {
                      clientRoute: meta.clientRoute as string | undefined,
                      apiPath: meta.apiPath as string | undefined,
                      referer: meta.referer as string | undefined,
                      source: meta.source as string | undefined,
                    }
                  : null
              }
            />
          )}
          {showDetails && hasChanges && (
            <div className="bg-white border border-slate-100/80 rounded-xl p-2.5 max-sm:p-2 hover:shadow-sm transition duration-200">
              <AuditChangesTrigger changes={row.changes} />
            </div>
          )}
        </div>
      )}
    </BlockStack>
  );

  if (padded) {
    return (
      <Box paddingBlockStart="200" paddingBlockEnd="200">
        {content}
      </Box>
    );
  }

  return content;
}
